import React from 'react';
import {Image, Platform, StyleSheet, TouchableOpacity, View, ViewStyle} from 'react-native';

import Text from '../../components/ui-controls/Text';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import headerLogo from '../../images/brand/header-logo.png';
import theme from '../../style/theme';

export interface ForceUpgradeScreenContentProps {
  /** Current app version to display */
  currentVersion: string;
  /** Minimum required version to display */
  minimumVersion: string;
  /** Callback when update button is pressed */
  onUpdatePress: () => void;
}

/**
 * Presentational component for the force upgrade screen
 * Pure component - receives all data via props, no state management
 */
const ForceUpgradeScreenContent = ({
  currentVersion,
  minimumVersion,
  onUpdatePress,
}: ForceUpgradeScreenContentProps) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const platformStyles = getPlatformStyles(isWebDesktopScreen);

  return (
    <View style={styles.container} testID="forceUpgradeScreen">
      {/* Blurred/darkened overlay background */}
      <View style={styles.overlay} />

      <View style={[styles.cardWrapper, platformStyles.cardWrapper]}>
        <View style={styles.card}>
          {/* Header with brand primary background and logo */}
          <View style={styles.cardHeader}>
            <Image
              source={headerLogo}
              style={[styles.logo, platformStyles.logo]}
              resizeMode="contain"
            />
          </View>

          {/* Content */}
          <View style={styles.content}>
            <Text style={styles.title}>Update required</Text>

            <Text style={styles.message}>
              A new version of the app is available. Please update to continue using all features.
            </Text>

            <Text style={styles.secondaryMessage}>
              If you&apos;ve already updated, please refresh the app.
            </Text>

            <TouchableOpacity
              style={styles.button}
              onPress={onUpdatePress}
              testID="updateButton"
              activeOpacity={0.8}>
              <Text style={styles.buttonText}>Update now</Text>
            </TouchableOpacity>

            {/* Horizontal divider */}
            <View style={styles.divider} />

            <Text style={styles.versionText}>Your version: {currentVersion}</Text>
            <Text style={styles.versionText}>Required: {minimumVersion} or later</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export default ForceUpgradeScreenContent;

const CARD_WIDTH_MOBILE = 340;
const CARD_WIDTH_DESKTOP = 480;
const LOGO_HEIGHT = 48;

const getPlatformStyles = (isWebDesktopScreen: boolean) =>
  StyleSheet.create({
    cardWrapper: {
      width: isWebDesktopScreen ? CARD_WIDTH_DESKTOP : CARD_WIDTH_MOBILE,
    },
    logo: {
      width: isWebDesktopScreen ? CARD_WIDTH_DESKTOP / 2 : CARD_WIDTH_MOBILE / 2,
    },
  });

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colour.general.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: Platform.OS === 'web' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.5)',
    ...(Platform.OS === 'web'
      ? ({
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        } as ViewStyle)
      : {}),
  },
  cardWrapper: {
    alignItems: 'center',
    maxWidth: '90%',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 2,
    borderColor: theme.colour.general.secondary,
    width: '100%',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 10,
  },
  cardHeader: {
    backgroundColor: theme.colour.background.general.primary,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logo: {
    height: LOGO_HEIGHT,
  },
  content: {
    paddingTop: 24,
    paddingHorizontal: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: theme.colour.text.heading.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 12,
  },
  secondaryMessage: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  button: {
    backgroundColor: theme.colour.uiControls.primary,
    paddingHorizontal: 48,
    paddingVertical: 14,
    borderRadius: 28,
    minWidth: 180,
    marginBottom: 20,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: '#E5E7EB',
    marginBottom: 16,
  },
  versionText: {
    fontSize: 13,
    color: '#9CA3AF',
    textAlign: 'center',
  },
});
